"use client";

import { Add, Check, Clear, FilterList } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import type { Column } from "@tanstack/react-table";
import * as React from "react";

import { ARRAY_SEPARATOR } from "@/lib/table";

type Option = {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  count?: number;
};

type DataTableFacetedFilterProps<TData, TValue> = {
  column?: Column<TData, TValue>;
  title?: string;
  options: Option[];
  multiple?: boolean;
};

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  multiple,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [query, setQuery] = React.useState("");

  const columnFilterValue = column?.getFilterValue();
  const selectedValues = new Set(
    Array.isArray(columnFilterValue)
      ? columnFilterValue
      : typeof columnFilterValue === "string"
        ? columnFilterValue.split(ARRAY_SEPARATOR)
        : [columnFilterValue].filter(Boolean)
  );

  const filteredOptions = React.useMemo(() => {
    const lowerQuery = query.toLowerCase().trim();
    if (!lowerQuery) return options;
    return options.filter((option) => option.label.toLowerCase().includes(lowerQuery));
  }, [options, query]);

  const onItemSelect = React.useCallback(
    (option: Option, isSelected: boolean) => {
      if (!column) return;

      if (multiple) {
        const next = new Set(selectedValues);
        if (isSelected) {
          next.delete(option.value);
        } else {
          next.add(option.value);
        }
        const nextValues = Array.from(next);
        column.setFilterValue(nextValues.length ? nextValues.join(ARRAY_SEPARATOR) : undefined);
        return;
      }

      column.setFilterValue(isSelected ? undefined : option.value);
      setAnchorEl(null);
    },
    [column, multiple, selectedValues]
  );

  const onReset = React.useCallback(() => {
    column?.setFilterValue(undefined);
  }, [column]);

  return (
    <>
      <Button
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
        startIcon={selectedValues.size > 0 ? <Clear /> : <Add />}
        sx={{ textTransform: "none", borderStyle: "dashed" }}
        variant="outlined"
      >
        {title}
        {selectedValues.size > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 0.5 }}>
            <Divider flexItem orientation="vertical" />
            {selectedValues.size > 2 ? (
              <Chip label={`${selectedValues.size} selected`} size="small" variant="outlined" />
            ) : (
              options
                .filter((option) => selectedValues.has(option.value))
                .map((option) => (
                  <Chip key={option.value} label={option.label} size="small" variant="outlined" />
                ))
            )}
          </Box>
        )}
      </Button>
      <Popover anchorEl={anchorEl} onClose={() => setAnchorEl(null)} open={Boolean(anchorEl)}>
        <Paper sx={{ p: 1, width: 240 }}>
          <TextField
            fullWidth
            onChange={(event) => setQuery(event.target.value)}
            placeholder={title}
            size="small"
            value={query}
          />
          <List dense sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
            {filteredOptions.length === 0 ? (
              <Typography color="text.secondary" sx={{ px: 1, py: 1 }} variant="body2">
                No results found.
              </Typography>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.has(option.value);

                return (
                  <ListItemButton
                    key={option.value}
                    onClick={() => onItemSelect(option, isSelected)}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Checkbox checked={isSelected} icon={<span />} size="small" />
                    </ListItemIcon>
                    <ListItemText primary={option.label} />
                    {option.count ? (
                      <Typography color="text.secondary" variant="caption">
                        {option.count}
                      </Typography>
                    ) : null}
                    {isSelected ? <Check fontSize="small" /> : null}
                  </ListItemButton>
                );
              })
            )}
          </List>
          {selectedValues.size > 0 && (
            <>
              <Divider />
              <Button
                fullWidth
                onClick={onReset}
                size="small"
                startIcon={<FilterList />}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Clear filters
              </Button>
            </>
          )}
        </Paper>
      </Popover>
    </>
  );
}
